import { adbApi, filesApi } from '@/lib/api';
import {
  parseInstallConflict,
  useInstallTasks,
  type InstallTask,
  type InstallTaskResult,
} from '@/store/installTasks';

/**
 * Side-table mapping task id → original File. Files can't live in
 * Zustand state because they aren't serializable and they short-
 * circuit DevTools / persistence middleware. They also have
 * non-trivial lifetimes — once the browser GCs the drop event,
 * the File slot becomes a Blob that may or may not still be
 * readable. We hold a strong ref here so the dock's retry / force-
 * downgrade / uninstall-and-retry actions all work even after the
 * user navigates between pages.
 */
const taskFiles = new Map<string, File>();

export function attachTaskFile(taskId: string, file: File): void {
  taskFiles.set(taskId, file);
}

export function getTaskFile(taskId: string): File | null {
  return taskFiles.get(taskId) ?? null;
}

export function releaseTaskFile(taskId: string): void {
  taskFiles.delete(taskId);
}

/**
 * Headless executor for install tasks. Lives outside React so:
 *
 *   - The drop zone can fire-and-forget after enqueue.
 *   - The dock card can call `runRetry` / `runUninstallAndRetry` /
 *     `runForceDowngrade` on click without re-mounting any provider.
 *   - Navigation between pages doesn't cancel the install.
 *
 * Everything here is purely procedural; the only state mutation goes
 * through `useInstallTasks.getState().update(taskId, patch)` so the
 * UI updates reactively via Zustand subscriptions.
 */

interface InstallStartOpts {
  file: File;
  /** When set, the runner reuses an already-uploaded server-side
   *  path instead of re-uploading. Used by the retry actions. */
  reusePath?: string;
  reinstall?: boolean;
  downgrade?: boolean;
}

interface BackendXapkResult {
  package: string;
  versionCode?: number;
  splits: { id: string; bytes: number }[];
  obb: { remote: string; bytes: number; pushed: boolean; warning?: string }[];
  durationMs: number;
}

/**
 * Start (or restart) an install task. Updates the store as it
 * progresses through `uploading` → `installing` → `success | conflict
 * | failed`. Errors thrown by the upload / adb step are caught and
 * converted into a terminal stage; callers don't need to wrap.
 *
 * Returns a promise that resolves when the task reaches a terminal
 * stage. The promise NEVER rejects — failures are recorded in the
 * store so the dock card UI is the single source of truth.
 */
export async function runInstallTask(taskId: string, opts: InstallStartOpts): Promise<void> {
  const { update } = useInstallTasks.getState();
  const task = useInstallTasks.getState().get(taskId);
  if (!task) return;
  const { serial, ext } = task;

  try {
    let serverPath = opts.reusePath ?? task.serverPath;
    if (!serverPath) {
      update(taskId, { stage: 'uploading', statusText: `上传 ${task.fileName}` });
      const { meta } = await filesApi.upload({
        serial,
        // 'apks' kind groups every APK-shaped artefact (drag-drop
        // uploads + app.pullApk exports) under
        // `<root>/<serial>/apks/`, sibling of screenshots/recordings/
        // logcat. Before 2026-05 these landed in `recordings/` and
        // cluttered the Recordings view; the dedicated kind keeps
        // the Files page clean. (Renamed 'installs' → 'apks' 2026-06
        // with the captures → devices rebrand.)
        kind: 'apks',
        ext,
        // Preserve the source filename verbatim (`Tap Tile!_1.0.5_APKPure.xapk`)
        // so the dock card, retry actions, and Files page all show the
        // name the user actually dropped. Server appends ` (N)` suffix on
        // collision. `prefix` stays as a defensive fallback in case
        // `filename` is empty (shouldn't happen via drag-drop but the
        // server route allows omitting `name`).
        filename: task.fileName,
        prefix: 'drop',
        blob: opts.file,
      });
      serverPath = meta.path;
    }
    // Pin the resolved server path onto the task so all subsequent
    // retry actions short-circuit the upload branch above. Critical:
    // without this, every "重试" / "强制降级" / "卸载并重装" click
    // re-uploads the same blob, and `resolveUnique` suffixes the
    // filename — so one drop + N retries leaves N+1 copies on disk.
    if (task.serverPath !== serverPath) {
      update(taskId, { serverPath });
    }
    update(taskId, {
      stage: 'installing',
      statusText:
        ext === 'xapk' ? '解压并安装（splits + OBB）' : opts.downgrade ? '强制降级安装' : '安装中',
    });

    if (ext === 'apk') {
      await adbApi.run({
        serial,
        name: 'app.install',
        args: {
          apkPath: serverPath,
          reinstall: opts.reinstall ?? true,
          ...(opts.downgrade ? { downgrade: true } : {}),
        },
      });
      const result: InstallTaskResult = { durationMs: Date.now() - task.startedAtMs };
      update(taskId, { stage: 'success', statusText: '安装成功', result });
    } else {
      const res = await adbApi.run<BackendXapkResult>({
        serial,
        name: 'app.installXapk',
        args: {
          xapkPath: serverPath,
          reinstall: opts.reinstall ?? true,
          ...(opts.downgrade ? { downgrade: true } : {}),
        },
      });
      const r = res.result;
      const obbPushed = r.obb.filter((o) => o.pushed).length;
      const obbFailed = r.obb.length - obbPushed;
      const parts = [`${r.package}`, `${r.splits.length} splits`];
      if (r.obb.length > 0) parts.push(`${obbPushed}/${r.obb.length} OBB`);
      update(taskId, {
        stage: 'success',
        statusText: parts.join(' · ') + (obbFailed > 0 ? '（部分 OBB 失败）' : ''),
        result: {
          package: r.package,
          ...(r.versionCode !== undefined ? { versionCode: r.versionCode } : {}),
          splits: r.splits,
          obb: r.obb,
          durationMs: r.durationMs,
        },
      });
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    const conflict = parseInstallConflict(msg);
    if (conflict) {
      update(taskId, {
        stage: 'conflict',
        conflict,
        error: msg,
        statusText: conflictHeadline(conflict.kind),
      });
    } else {
      update(taskId, { stage: 'failed', error: msg, statusText: '安装失败' });
    }
  }
}

function conflictHeadline(kind: string): string {
  switch (kind) {
    case 'version_downgrade':
      return '版本降级被拒绝';
    case 'signature_mismatch':
      return '签名不一致';
    case 'sdk_mismatch':
      return 'SDK 不兼容';
    case 'already_exists':
      return '应用已存在';
    default:
      return '安装失败';
  }
}

/**
 * Install an APK / XAPK that is **already** sitting in the devices
 * tree on the server (e.g. a previous drop-drop upload, a file the
 * user pulled via `app.pullApk`, or any artefact in the Files page's
 * "拖拽安装包" / "installs" view). No upload happens — we just call
 * `app.install` / `app.installXapk` directly against the existing
 * server path.
 *
 * Returns the enqueued task id so the caller can show a confirmation
 * toast / open the dock card if it wants to.
 *
 * Note: there is no `File` attached to this task, so the dock's
 * "重试" / "卸载并重装" actions that need to re-upload will gracefully
 * report `false` (the user can re-trigger from the preview modal).
 * Conflict actions that don't need a fresh upload — `force-downgrade`
 * via `app.install --downgrade=true` against the same server path —
 * still work.
 */
export function runInstallExistingTask(opts: {
  serverPath: string;
  ext: 'apk' | 'xapk';
  serial: string;
  fileName: string;
  serialLabel?: string;
  reinstall?: boolean;
  downgrade?: boolean;
}): string {
  const { enqueue } = useInstallTasks.getState();
  const taskId = enqueue({
    fileName: opts.fileName,
    ext: opts.ext,
    serial: opts.serial,
    ...(opts.serialLabel ? { serialLabel: opts.serialLabel } : {}),
    stage: 'queued',
    statusText: '准备安装',
  });
  // Fire-and-forget; runInstallTask never rejects (errors land in the
  // store as a 'failed' / 'conflict' stage).
  void runInstallTask(taskId, {
    // A dummy zero-byte File satisfies `runInstallTask`'s signature.
    // It's never read because `reusePath` short-circuits the upload
    // branch, and `attachTaskFile` keeps it around only for retries
    // (which we can't do without a real source file anyway).
    file: new File([], opts.fileName),
    reusePath: opts.serverPath,
    reinstall: opts.reinstall ?? true,
    ...(opts.downgrade ? { downgrade: true } : {}),
  });
  return taskId;
}

/**
 * Re-run the install. Reuses the server-side file from the first
 * upload (recorded as `task.serverPath`) so N retries don't leak N+1
 * copies into the devices directory. Falls back to a fresh upload
 * only when `serverPath` is missing (defensive — should never happen
 * for tasks created via the standard drop / preview-install paths).
 *
 * Returns `false` only when both the cached File **and** the recorded
 * `serverPath` are unavailable, which means we have nothing to install
 * — the user must drop the file again.
 */
export function retryTask(task: InstallTask): boolean {
  const file = getTaskFile(task.id);
  if (!file && !task.serverPath) return false;
  useInstallTasks.getState().update(task.id, {
    stage: 'queued',
    error: undefined,
    conflict: undefined,
    statusText: '准备重试',
    startedAtMs: Date.now(),
  });
  void runInstallTask(task.id, {
    file: file ?? new File([], task.fileName),
    ...(task.serverPath ? { reusePath: task.serverPath } : {}),
    reinstall: true,
  });
  return true;
}

/**
 * Force-downgrade retry: re-runs the install with `downgrade: true`
 * (adb's `-d` flag). Only meaningful for `version_downgrade`
 * conflicts. Reuses `task.serverPath` so we don't pile up duplicate
 * uploads in the devices dir — see `retryTask` for the rationale.
 */
export function forceDowngradeRetry(task: InstallTask): boolean {
  const file = getTaskFile(task.id);
  if (!file && !task.serverPath) return false;
  useInstallTasks.getState().update(task.id, {
    stage: 'queued',
    error: undefined,
    conflict: undefined,
    statusText: '准备强制降级',
    startedAtMs: Date.now(),
  });
  void runInstallTask(task.id, {
    file: file ?? new File([], task.fileName),
    ...(task.serverPath ? { reusePath: task.serverPath } : {}),
    reinstall: true,
    downgrade: true,
  });
  return true;
}

/**
 * Uninstall the existing package, then re-run the install. Used for
 * `signature_mismatch` (the only fix) and as an explicit option for
 * `version_downgrade` users who prefer a clean slate over `-d`.
 *
 * On uninstall failure the task is left in `failed` with the adb
 * error message so the user can see what happened (and try the OS
 * settings app as a manual fallback).
 */
export async function uninstallAndRetry(task: InstallTask): Promise<boolean> {
  const file = getTaskFile(task.id);
  if (!file && !task.serverPath) return false;
  const pkg = task.conflict?.packageName;
  const { update } = useInstallTasks.getState();
  if (!pkg) {
    update(task.id, {
      stage: 'failed',
      error: '后端未识别包名，无法自动卸载。请到「应用」页手动卸载后重试。',
    });
    return false;
  }
  update(task.id, {
    stage: 'installing',
    statusText: `卸载旧版 ${pkg}`,
    error: undefined,
    conflict: undefined,
    startedAtMs: Date.now(),
  });
  try {
    await adbApi.run({
      serial: task.serial,
      name: 'app.uninstall',
      args: { package: pkg },
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    update(task.id, { stage: 'failed', error: `卸载失败：${msg}`, statusText: '卸载失败' });
    return false;
  }
  update(task.id, { statusText: '卸载成功，重新安装中' });
  await runInstallTask(task.id, {
    file: file ?? new File([], task.fileName),
    ...(task.serverPath ? { reusePath: task.serverPath } : {}),
    reinstall: true,
  });
  return true;
}
